import * as React from 'react';
import handleOnSettingsChange from 'bootstrap/lib/handleOnSettingsChange';
import initializeBootLocalization from 'mini-boot-localization/lib/initializeBootLocalization';
import initializeMailModuleState from './actions/initializeMailModuleState';
import initialMailSetup from './actions/initialMailSetup';
import MailModuleWrapper from 'mini-mail-module-view/lib/components/MailModuleWrapper';
import OwaWorkload from 'owa-workloads/lib/store/schema/OwaWorkload';
import sharedBootstrap from 'owa-shared-bootstrap/lib/bootstrap';
import strings from 'owa-strings/owa-mini-strings';
import { AppName } from 'data/lib/mini-app-constants';
import { calendarIconsRegistration } from 'mini-calendar-icons';
import { getBootstrapConfig } from 'bootstrap/lib/getBootstrapConfig';
import { getMiniAnalyticsOptions } from 'mini-app-analytics';
import { getWhitelistedSessionDataSettings } from 'mini-settings/lib/getWhitelistedSettings';
import { getWindowTitle } from 'mini-window-title/lib/getWindowTitle';
import { initializeFeatureFlagsFromUserBootSettings } from 'bootstrap/lib/initializeFeatureFlagsFromUserBootSettings';
import { initializeMailRoutes, mailRouterOptions } from './routing/mailRoutes';
import { mailIconsRegistration } from 'mini-mail-icons';
import { MiniErrorComponentAsync } from 'mini-error-component';
import { miniIconsRegistration } from 'owa-mini-icons';
import { onUnloadMailWindow } from './onUnloadMailWindow';
import { postMailBootstrap, postMailBootstrapOneTimeSetup } from './postMailBootstrap';
import { SessionData } from 'owa-service/lib/types/SessionData';
import { setApp } from 'owa-config/lib/bootstrapOptions';
import { setDocumentDirection } from 'bootstrap/lib/setDocumentDirection';
import { setupExceptionHandler } from 'mini-exception-handler';
import { USE_STARTUP_DATA_FROM_EXTENSION } from 'extension-communication/lib/constants/useDataFromExtension';
import {
    registerSessionStorageSetters,
    saveSessionDataFromNetwork,
} from 'bootstrap/lib/bootstrapDataSetters';

const hideLoadingScreenIfPresent = () => {
    const loadingScreen = document.getElementById('loadingScreen');
    if (loadingScreen && loadingScreen.parentNode === document.body) {
        document.body.removeChild(loadingScreen);
    }
};

export default async function bootstrapMailModule(
    sessionPromise: Promise<SessionData>
): Promise<void> {
    setApp(AppName.MAIL);

    // Execute as early as possible to catch errors
    setupExceptionHandler();

    const bootstrapConfigExtended = getBootstrapConfig(sessionPromise, OwaWorkload.Mail);

    if (USE_STARTUP_DATA_FROM_EXTENSION) {
        const [staleSessionDataFromSessionStorage] = await Promise.all([
            bootstrapConfigExtended.sessionDataPromise.then(
                initializeFeatureFlagsFromUserBootSettings
            ),
        ]);
        // setUserConfiguration(staleSessionDataFromSessionStorage.owaUserConfig); // TODO: See if this is needed
        setDocumentDirection(staleSessionDataFromSessionStorage);
    } else {
        /** The sessionPromise in Mailmodule is always from network call and we want to cache the value
         * to read the pruned userconfig from shared start before we set it in sessionstorage
         */
        sessionPromise.then(sessionData => {
            saveSessionDataFromNetwork(sessionData);
            setDocumentDirection(sessionData);
        });
    }

    // Do all initial setup like initialize Logger, add listeners, register middlewares etc
    // that are needed before bootstrap starts.
    initialMailSetup();

    /**
     * This sharedBootstrap is the main method which does the module bootstrap with passed data in initialConfig.
     * Data kept in sessionStorage is used by this method to boot up the module
     */
    const initialBootstrapPromise = sharedBootstrap({
        initializeState: initializeMailModuleState as any,
        renderMainComponent: () => <MailModuleWrapper />,
        sessionPromise: bootstrapConfigExtended.sessionDataPromise,
        registerRoutes: initializeMailRoutes(),
        routerOptions: mailRouterOptions,
        getWindowTitle: () => getWindowTitle(strings.mailModuleName),
        workload: OwaWorkload.Mail,
        analyticsOptions: getMiniAnalyticsOptions(),
        iconFonts: [miniIconsRegistration, mailIconsRegistration, calendarIconsRegistration],
        initializeLocalization: initializeBootLocalization(OwaWorkload.Mail),
        fullErrorComponent: MiniErrorComponentAsync,
        shouldInitializeTimeZoneAnonymously: false,
    }).then(() => {
        hideLoadingScreenIfPresent();
        postMailBootstrapOneTimeSetup();
        postMailBootstrap();
    });

    if (USE_STARTUP_DATA_FROM_EXTENSION) {
        /** This is fired afterwords to store network response data in sessionStorage for faster subsequent boot */
        registerSessionStorageSetters(
            initialBootstrapPromise,
            sessionPromise,
            onUnloadMailWindow /** On unload window */,
            onModuleRebootstrap /** Module rebootstrap callback */,
            OwaWorkload.Mail,
            bootstrapConfigExtended.hasDataInSessionStorage,
            getWhitelistedSessionDataSettings(),
            onSessionDataFromNetwork,
            handleOnSettingsChange
        );
    } else {
        registerSessionStorageSetters(
            null /** Passing null for initial bootstrap promise  */,
            sessionPromise,
            null /** On unload window */,
            null /** Module rebootstrap callback */,
            OwaWorkload.Mail,
            bootstrapConfigExtended.hasDataInSessionStorage,
            {}
        );
    }

    return initialBootstrapPromise;
}

// TODO: Implement this when boot will happen from the data provided by extension
/**
 * Callback function to be passed to perform mail module specific functions when the network sessiondata is received
 * @param sessionData
 */
const onSessionDataFromNetwork = (sessionData: SessionData) => {
    // tslint:disable-next-line: no-console
    console.log('sessiondata from network', sessionData);

    // Perform any action needed to run immediately after network API call is complete
};

/**
 * TODO: Implement this when boot will happen from the data provided by extension
 * Mail Specific rebootstrap
 * This will perform all the actions irrespective of any settings change or not
 */
const onModuleRebootstrap = async ({ sessionData }) => {
    // TODO: Update the view state/store with the latest data from server
    // tslint:disable-next-line: no-console
    console.log('Inside mail module rebootstrap');
    // stashCalendarFoldersFromSessionData(sessionData);
    // setUserConfigurationCalendarSelection(
    //     (sessionData as SessionData)?.owaUserConfig?.ViewStateConfiguration
    //         ?.SelectedCalendarsTNarrow
    // );
    // setUserConfigurationCategories((sessionData as SessionData)?.owaUserConfig?.MasterCategoryList);

    // await createPreRenderPromise();
    // await calendarInitAfterBootLocalization();
    postMailBootstrap();
};
